import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { showApiErrorToast } from '@/utils/errors';
import {
  approveLeaveApplication,
  leaveApplicationQueryKeys,
  rejectLeaveApplication,
} from '../api/leaveApplications.api';
import { REVIEW_DECISION } from '../constants/leaveApplication.constants';
import type {
  ReviewDecision,
  ReviewLeaveApplicationPayload,
} from '../types/leaveApplication.types';

interface ReviewArgs {
  id: number;
  decision: ReviewDecision;
  payload: ReviewLeaveApplicationPayload;
}

const REVIEW_MESSAGE: Record<ReviewDecision, { success: string; failure: string }> = {
  APPROVE: {
    success: 'Leave application approved',
    failure: 'Failed to approve the leave application',
  },
  REJECT: {
    success: 'Leave application rejected',
    failure: 'Failed to reject the leave application',
  },
};

export function useReviewLeaveApplication(onSuccess?: () => void) {
  const queryClient = useQueryClient();

  function refreshReviewedApplication(id: number) {
    const queryKeys = [
      leaveApplicationQueryKeys.lists(),
      leaveApplicationQueryKeys.summaries(),
      leaveApplicationQueryKeys.detail(id),
      leaveApplicationQueryKeys.usageSummaries(),
    ];
    for (const queryKey of queryKeys) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }

  return useMutation({
    mutationFn: ({ id, decision, payload }: ReviewArgs) =>
      decision === REVIEW_DECISION.APPROVE
        ? approveLeaveApplication(id, payload)
        : rejectLeaveApplication(id, payload),
    onSuccess: (_data, { id, decision }) => {
      refreshReviewedApplication(id);
      toast.success(REVIEW_MESSAGE[decision].success);
      onSuccess?.();
    },
    onError: (error, { id, decision }) => {
      refreshReviewedApplication(id);
      showApiErrorToast(error, REVIEW_MESSAGE[decision].failure);
    },
  });
}
